import React, { Component } from 'react'
import { withRouter } from 'react-router'
import { observer, inject } from 'mobx-react'
import moment from 'moment'
import CsvUploader from '@components/Popup/CsvUploader'
import { extractErrorMessage } from '@components/Popup/CsvUploader/Utils/extractErrorMessage'
import replacedRows from '@services/replacedRows'
import { SUBSCRIPTION_STATUS_LABELS } from '@constants/subscriptionStatuses'
import { openStoSearch, pruneStoSearchHandoffs } from '@utils/stoSearchHandoff'

import {
    Layout,
    PageHeader,
    Button,
    Icon,
    Table,
    Radio,
    Input,
    DatePicker,
    message,
    Tag,
    Tooltip,
    Pagination,
    Dropdown,
    Menu,
    Switch
} from 'antd'

import { tableClass } from '@pages/AgentCommissions/Reports/style.scss'

const { RangePicker } = DatePicker

// ── types ───────────────────────────────────────────────────
type ListMode = 'errors' | 'apply_require' | 'done' | 'unfrozen_check' | 'all'
type UpdateTypeFilter = 'all' | 'blocked' | 'unfrozen'
type ArchiveType = 'error' | 'process'
type ArchiveActionKey = 'sto' | 'block' | 'cancellation'
type ReplacedCardStatus = 'APPLY_REQUIRE' | 'HANDLE_REQUIRE' | 'NO_HANDLE_REQUIRE' | 'HANDLED'

interface ArchiveAction {
    checked: boolean
    done: boolean
    error?: string
    skip_reason?: 'manual_required' | 'not_required' | string
    resolved?: boolean
    resolved_by?: string | null
    resolved_at?: string | null
    resolve_status?: string
}
interface ArchiveEntry {
    sto_id: string | number | null
    sto: ArchiveAction
    block: ArchiveAction
    cancellation: ArchiveAction
    resolved: boolean
}
interface ProcessArchive {
    apply_actions_process: ArchiveEntry[]
}
interface ErrorArchiveEntry {
    sto_id: string | number | null
    err_key: string
    error_message: string
    resolved: boolean
}

interface LinkUser {
    user_id: number
    sto_id: string | number
    user_sub_id: number | null
    terminal_name: string
    status: string | null
    user_name: string | null
}

interface ReplacedCardRow {
    id: number
    created_at: string
    batch_date: string | null
    status: ReplacedCardStatus
    terminal_name: string
    update_type: string
    processing_result: string
    existing_four_digits: string | null
    existing_token: string | null
    error_archive: ErrorArchiveEntry[] | null
    applied_at: string | null
    applied_by: string | null
    handled_at: string | null
    handled_by: string | null
    has_error: boolean | number
    process_archive: ProcessArchive | null
    users: LinkUser[]
}

interface FlatUnresolvedItem {
    archive: ArchiveType
    index: number
    text: string
    resolved: boolean
}

interface FlatErrorItem {
    index: number
    err_key: string
    error_message: string
    resolved: boolean
}

interface FlatStoRow {
    key: string
    record_id: number
    sto_id: string | number | null
    created_at: string
    batch_date: string | null
    status: ReplacedCardStatus
    terminal_name: string
    update_type: string
    processing_result: string
    existing_four_digits: string | null
    existing_token: string | null
    applied_by: string | null
    applied_at: string | null
    handled_by: string | null
    handled_at: string | null
    has_error: boolean | number
    user_id: number | null
    user_name: string | null
    user_sub_id: number | null
    sub_status: string | null
    unresolvedItems: FlatUnresolvedItem[]
    errorItems: FlatErrorItem[]
    processEntry: ArchiveEntry | null
    processIndex: number | null
    allErrorsResolved: boolean
}

interface IState {
    openCsvUploaderModal: boolean
    applyRequireDates: Array<{ date: string; count: number }>
    applyModalOpen: boolean
    loading: boolean
    rows: ReplacedCardRow[]
    total: number
    page: number
    perPage: number
    mode: ListMode
    searchUpdateType: UpdateTypeFilter
    searchUserId: string
    searchStoId: string
    searchToken: string
    searchFourDigits: string
    searchDateFrom: moment.Moment | null
    searchDateTo: moment.Moment | null
}

const ACTION_LABELS: Array<{ key: ArchiveActionKey; label: string }> = [
    { key: 'sto', label: 'ביטול בטרנזילה' },
    { key: 'block', label: 'חסימת מנוי' },
    { key: 'cancellation', label: 'בקשת ביטול' }
]

const RESOLVE_STATUS_OPTIONS: Array<{ key: string; label: string; color?: string }> = [
    // { key: 'done', label: 'בוצע', color: 'green' } not in use
    { key: 'handled', label: 'טופל ידנית', color: 'green' },
    { key: 'no_handle_need', label: 'לא נדרש טיפול', color: undefined },
    { key: 'error', label: 'לא בוצע', color: 'red' }
]

// The backend refuses handleReplacedCard while a record is still APPLY_REQUIRE
// (ReplacedController: 'לא ניתן לטפל ברשומה זו'), so the resolve controls are shown
// but disabled on those rows instead of failing on click.
const RESOLVE_LOCKED_TOOLTIP = 'יש לבצע את הפעולות תחילה'

const FILTER_UNAVAILABLE_TOOLTIP = 'הסינון אינו זמין עבור מצב זה'

// The two filter groups can contradict each other; the mode always wins. 'errors' excludes unfrozen
// by design, and 'unfrozen_check' is unfrozen-only — either pairing would always return zero rows.
const isUpdateTypeDisabled = (mode: ListMode, updateType: UpdateTypeFilter): boolean => {
    if (mode === 'unfrozen_check') return updateType !== 'all'
    if (mode === 'errors') return updateType === 'unfrozen'
    return false
}

const UPDATE_TYPE_LABELS: { [key: string]: string } = {
    blocked: 'חסימה',
    unfrozen: 'הפשרה'
}

const PROCESSING_RESULT_LABELS: { [key: string]: string } = {
    applied: 'בוצע',
    skipped: 'דילג'
}

const TABLE_COLUMN_WIDTHS = {
    created_at: 122,
    update_type: 132,
    existing_four_digits: 88,
    status: 112,
    user_id: 132,
    sto_id: 112,
    sub_status: 116,
    errorItems: 260,
    processEntry: 320,
    applied_by: 132,
    applied_at: 142,
    handled_by: 142,
    handled_at: 142
}
const TABLE_SCROLL_X = Object.values(TABLE_COLUMN_WIDTHS).reduce((a, b) => a + b, 0)

function tokenColor(id: number): string {
    const normalizedId = Math.abs(Math.floor(id || 0))
    const h = (normalizedId * 137 + 29) % 360
    return `hsla(${h}, 78%, 82%, 0.4)`
}

const sameStoId = (left: string | number | null | undefined, right: string | number | null | undefined): boolean => {
    const leftEmpty = left === null || left === undefined
    const rightEmpty = right === null || right === undefined
    if (leftEmpty || rightEmpty) return leftEmpty && rightEmpty
    return String(left) === String(right)
}

const addStoId = (ids: Array<string | number>, stoId: string | number | null | undefined) => {
    if (stoId === null || stoId === undefined) return
    const exists = ids.some(id => sameStoId(id, stoId))
    if (!exists) ids.push(stoId)
}

const getErrorArchive = (record: ReplacedCardRow): ErrorArchiveEntry[] => {
    return Array.isArray(record.error_archive) ? record.error_archive : []
}

const getProcessEntries = (record: ReplacedCardRow): ArchiveEntry[] => {
    if (!record.process_archive || !Array.isArray(record.process_archive.apply_actions_process)) return []
    return record.process_archive.apply_actions_process
}

const getProcessEntryForSto = (stoId: string | number | null, processEntries: ArchiveEntry[]): ArchiveEntry | null => {
    return processEntries.filter(processEntry => sameStoId(processEntry.sto_id, stoId))[0] || null
}

const getProcessIndexForSto = (stoId: string | number | null, processEntries: ArchiveEntry[]): number | null => {
    const index = processEntries.findIndex(processEntry => sameStoId(processEntry.sto_id, stoId))
    return index === -1 ? null : index
}

const getErrorItemsForSto = (stoId: string | number | null, errorArchive: ErrorArchiveEntry[]): FlatErrorItem[] => {
    const errorItems: FlatErrorItem[] = []
    errorArchive.forEach((errorEntry, index) => {
        if (sameStoId(errorEntry.sto_id, stoId)) {
            errorItems.push({
                index,
                err_key: errorEntry.err_key,
                error_message: errorEntry.error_message,
                resolved: errorEntry.resolved === true
            })
        }
    })
    return errorItems
}

const getItemsForSto = (
    stoId: string | number | null,
    errorArchive: ErrorArchiveEntry[],
    processEntries: ArchiveEntry[]
): { unresolvedItems: FlatUnresolvedItem[]; hasErrorItems: boolean } => {
    const unresolvedItems: FlatUnresolvedItem[] = []
    let hasErrorItems = false

    errorArchive.forEach((errorEntry, index) => {
        if (sameStoId(errorEntry.sto_id, stoId)) {
            hasErrorItems = true
            if (errorEntry.resolved !== true) {
                unresolvedItems.push({
                    archive: 'error',
                    index,
                    text: errorEntry.error_message,
                    resolved: false
                })
            }
        }
    })

    processEntries.forEach((processEntry, index) => {
        if (sameStoId(processEntry.sto_id, stoId)) {
            ACTION_LABELS.forEach(actionLabel => {
                const action = processEntry[actionLabel.key]
                if (!action) return

                const isFailedChecked = action.checked === true && action.done !== true && action.resolved !== true
                const isManualRequired =
                    action.checked !== true && action.skip_reason === 'manual_required' && action.resolved !== true

                // Honor operator's resolve_status first (mirrors backend actionUnresolved).
                let isUnresolved: boolean
                if (action.resolve_status) {
                    isUnresolved = action.resolve_status === 'error'
                } else {
                    isUnresolved = isFailedChecked || isManualRequired
                }

                if (isUnresolved) {
                    hasErrorItems = true
                    unresolvedItems.push({
                        archive: 'process',
                        index,
                        text: isManualRequired
                            ? `${actionLabel.label}: נדרש טיפול ידני`
                            : `${actionLabel.label} נכשל: ${action.error || ''}`,
                        resolved: false
                    })
                } else if (isFailedChecked || isManualRequired) {
                    // resolve_status marked it resolved even though the system view sees an issue.
                    hasErrorItems = true
                }
            })
        }
    })

    return { unresolvedItems, hasErrorItems }
}

const buildFlatStoRow = (
    record: ReplacedCardRow,
    stoId: string | number | null,
    unresolvedItems: FlatUnresolvedItem[],
    errorItems: FlatErrorItem[],
    processEntry: ArchiveEntry | null,
    processIndex: number | null,
    hasErrorItems: boolean
): FlatStoRow => {
    const users = Array.isArray(record.users) ? record.users : []
    const matchingUsers = users.filter(user => sameStoId(user.sto_id, stoId))
    const user = stoId === null ? null : matchingUsers[0]
    return {
        key: `${record.id}-${stoId === null ? 'null' : String(stoId)}`,
        record_id: record.id,
        sto_id: stoId,
        created_at: record.created_at,
        batch_date: record.batch_date,
        status: record.status,
        terminal_name: record.terminal_name,
        update_type: record.update_type,
        processing_result: record.processing_result,
        existing_four_digits: record.existing_four_digits,
        existing_token: record.existing_token,
        applied_by: record.applied_by,
        applied_at: record.applied_at,
        handled_by: record.handled_by,
        handled_at: record.handled_at,
        has_error: record.has_error,
        user_id: user ? user.user_id : null,
        user_name: user ? user.user_name : null,
        user_sub_id: user ? user.user_sub_id : null,
        sub_status: user ? user.status : null,
        unresolvedItems,
        errorItems,
        processEntry,
        processIndex,
        allErrorsResolved: hasErrorItems && unresolvedItems.length === 0
    }
}

const flattenReplacedCardRows = (records: ReplacedCardRow[]): FlatStoRow[] => {
    const flatRows: FlatStoRow[] = []
    records.forEach(record => {
        const users = Array.isArray(record.users) ? record.users : []
        const errorArchive = getErrorArchive(record)
        const processEntries = getProcessEntries(record)
        const stoIds: Array<string | number> = []

        users.forEach(user => addStoId(stoIds, user.sto_id))
        errorArchive.forEach(errorEntry => addStoId(stoIds, errorEntry.sto_id))
        processEntries.forEach(processEntry => addStoId(stoIds, processEntry.sto_id))

        stoIds.forEach(stoId => {
            const items = getItemsForSto(stoId, errorArchive, processEntries)
            const errorItems = getErrorItemsForSto(stoId, errorArchive)
            flatRows.push(
                buildFlatStoRow(
                    record,
                    stoId,
                    items.unresolvedItems,
                    errorItems,
                    getProcessEntryForSto(stoId, processEntries),
                    getProcessIndexForSto(stoId, processEntries),
                    items.hasErrorItems
                )
            )
        })

        const nullStoItems = getItemsForSto(null, errorArchive, processEntries)
        const nullErrorItems = getErrorItemsForSto(null, errorArchive)
        const nullProcessEntry = getProcessEntryForSto(null, processEntries)
        if (
            nullStoItems.unresolvedItems.length > 0 ||
            nullErrorItems.length > 0 ||
            nullProcessEntry ||
            stoIds.length === 0
        ) {
            flatRows.push(
                buildFlatStoRow(
                    record,
                    null,
                    nullStoItems.unresolvedItems,
                    nullErrorItems,
                    nullProcessEntry,
                    getProcessIndexForSto(null, processEntries),
                    nullStoItems.hasErrorItems
                )
            )
        }
    })
    return flatRows
}

const pageStyles = `
    .replaced-cards-page {
        padding: 0 24px 24px;
        direction: rtl;
    }

    .replaced-cards-search {
        margin-bottom: 16px;
        direction: rtl;
    }

    .replaced-cards-filters {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        margin: -4px;
    }

    .replaced-cards-filters > * {
        margin: 4px;
    }

    .replaced-cards-filter-xs {
        width: 110px;
    }

    .replaced-cards-filter-sm {
        width: 130px;
    }

    .replaced-cards-filter-md {
        width: 170px;
    }

    .replaced-cards-date-filter {
        width: 240px;
    }

    .replaced-cards-table-shell {
        background: #fff;
        border: 1px solid #f0f0f0;
        border-radius: 4px;
        overflow: hidden;
    }

    .replaced-cards-table {
        direction: rtl;
    }

    .replaced-cards-table .ant-table-small {
        border: 0;
    }

    .replaced-cards-table .ant-table table {
        table-layout: fixed;
    }

    .replaced-cards-table .ant-table-body {
        overflow-x: auto !important;
    }

    .replaced-cards-table .ant-table-thead > tr > th {
        background: #fafafa;
        color: #595959;
        font-weight: 600;
        padding: 10px 8px;
        white-space: nowrap;
    }

    .replaced-cards-table .ant-table-tbody > tr > td {
        padding: 8px;
        vertical-align: middle;
    }

    .replaced-cards-table .ant-table-tbody > tr:hover > td {
        background: #f5fbff;
    }

    .replaced-cards-table .ant-table-pagination {
        margin: 12px 16px;
    }

    .replaced-cards-row-error td {
        background: #fffafa;
    }

    .replaced-cards-cell {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .replaced-cards-date {
        direction: ltr;
        text-align: center;
    }

    .replaced-cards-errors {
        direction: rtl;
    }

    .replaced-cards-error-item {
        align-items: center;
        border-bottom: 1px solid #f0f0f0;
        display: flex;
        justify-content: flex-start;
        padding: 4px 0;
    }

    .replaced-cards-error-item > * {
        margin-left: 4px;
    }

    .replaced-cards-error-item > *:last-child {
        margin-left: 0;
    }

    .replaced-cards-error-item .ant-btn {
        flex: 0 0 auto;
        height: 22px;
        line-height: 20px;
        padding: 0 6px;
    }

    .replaced-cards-error-message {
        flex: 0 1 auto;
        line-height: 18px;
        min-width: 0;
        white-space: normal;
    }

    .replaced-cards-error-resolve-button,
    .replaced-cards-process-resolve-button {
        background: #f6ffed;
        border-color: #b7eb8f;
        color: #389e0d;
        min-width: 22px;
        padding: 0;
        width: 22px;
    }

    .replaced-cards-error-resolve-button:hover,
    .replaced-cards-error-resolve-button:focus,
    .replaced-cards-process-resolve-button:hover,
    .replaced-cards-process-resolve-button:focus {
        background: #f6ffed;
        border-color: #52c41a;
        color: #237804;
    }

    .replaced-cards-error-message-resolved {
        color: #bfbfbf;
    }

    .replaced-cards-error-icon-unresolved {
        color: #f5222d;
    }

    .replaced-cards-error-icon-resolved {
        color: #bfbfbf;
    }

    .replaced-cards-error-item:last-child {
        border-bottom: 0;
    }

    .replaced-cards-user-cell {
        display: inline-flex;
        flex-direction: column;
        line-height: 18px;
    }

    .replaced-cards-user-name {
        color: #595959;
    }

    .replaced-cards-process-steps {
        align-items: flex-start;
        direction: rtl;
        display: flex;
        flex-direction: column;
        white-space: nowrap;
    }

    .replaced-cards-process-steps .ant-tag {
        flex: 0 0 auto;
        margin: 0 0 4px 0;
    }

    .replaced-cards-process-step-row {
        align-items: center;
        display: flex;
        margin-bottom: 4px;
    }

    .replaced-cards-process-step-row .ant-tag {
        margin-left: 4px;
        margin-bottom: 0;
    }

    .replaced-cards-process-step-row .ant-btn {
        height: 22px;
        line-height: 20px;
        padding: 0 6px;
    }

    .replaced-cards-process-step-row .replaced-cards-process-resolve-button {
        padding: 0;
    }

    .replaced-cards-table .replaced-cards-handled-by-column {
        border-left: 1px solid #d9d9d9 !important;
    }
`

@inject((store: any) => {
    const { authStore } = store
    return { authStore }
})
@observer
class ReplacedCards extends Component<any, IState> {
    state: IState = {
        openCsvUploaderModal: false,
        applyRequireDates: [],
        applyModalOpen: false,
        loading: false,
        rows: [],
        total: 0,
        page: 1,
        perPage: 20,
        mode: 'errors',
        searchUpdateType: 'all',
        searchUserId: '',
        searchStoId: '',
        searchToken: '',
        searchFourDigits: '',
        searchDateFrom: null,
        searchDateTo: null
    }

    // Generation counter for list fetches; a newer fetch bumps it so an older, slower
    // response is dropped instead of overwriting newer data.
    private fetchSeq: number = 0
    private _unmounted: boolean = false

    componentDidMount() {
        pruneStoSearchHandoffs()
        this.fetchRows(1)
        this.loadApplyRequireDates()
    }

    componentWillUnmount() {
        this._unmounted = true
    }

    loadApplyRequireDates = async () => {
        try {
            const res = await replacedRows.getApplyRequireDates()
            if (this._unmounted) return
            this.setState({ applyRequireDates: (res.data && res.data.dates) || [] })
        } catch (e) {
            if (this._unmounted) return
            this.setState({ applyRequireDates: [] })
        }
    }

    fetchRows = async (page = this.state.page) => {
        // Bump the fetch generation and capture it locally; a newer fetch supersedes this one.
        this.fetchSeq += 1
        const seq = this.fetchSeq
        this.setState({ loading: true })
        const {
            mode,
            searchUpdateType,
            searchUserId,
            searchStoId,
            searchToken,
            searchFourDigits,
            searchDateFrom,
            searchDateTo,
            perPage
        } = this.state
        const payload: any = { mode, page, per_page: perPage }
        if (searchUserId) payload.user_id = Number(searchUserId)
        if (searchUpdateType !== 'all') payload.update_type = searchUpdateType
        if (searchStoId) payload.sto_id = searchStoId
        if (searchToken) payload.token = searchToken
        if (searchFourDigits) payload.four_digits = searchFourDigits
        if (searchDateFrom) payload.date_from = searchDateFrom.format('YYYY-MM-DD')
        if (searchDateTo) payload.date_to = searchDateTo.format('YYYY-MM-DD')
        try {
            const res = await replacedRows.getReplacedCards(payload)
            // Drop a stale response (a newer fetch started) or a response arriving after unmount.
            if (this._unmounted || seq !== this.fetchSeq) return
            this.setState({
                rows: res.data.data || [],
                total: res.data.total || 0,
                page: res.data.page || page,
                loading: false
            })
        } catch (e) {
            if (this._unmounted || seq !== this.fetchSeq) return
            this.setState({ loading: false })
            message.error('שגיאה בטעינת נתונים')
        }
    }

    handleSearch = () => {
        this.setState({ page: 1 }, () => this.fetchRows(1))
    }

    handleModeChange = (mode: ListMode) => {
        // Drop a type the new mode contradicts in the SAME setState, so the refetch in the callback
        // never sends the contradictory pair.
        const searchUpdateType = isUpdateTypeDisabled(mode, this.state.searchUpdateType)
            ? 'all'
            : this.state.searchUpdateType
        this.setState({ mode, searchUpdateType, page: 1 }, () => this.fetchRows(1))
    }

    handleUpdateTypeChange = (searchUpdateType: UpdateTypeFilter) => {
        this.setState({ searchUpdateType, page: 1 }, () => this.fetchRows(1))
    }

    resetSearch = () => {
        this.setState(
            {
                mode: 'errors',
                searchUpdateType: 'all',
                searchUserId: '',
                searchStoId: '',
                searchToken: '',
                searchFourDigits: '',
                searchDateFrom: null,
                searchDateTo: null,
                page: 1
            },
            () => this.fetchRows(1)
        )
    }

    getResolveKey = (recordId: number, archive: ArchiveType, index: number, action?: ArchiveActionKey): string => {
        return `${recordId}-${archive}-${index}-${action || ''}`
    }

    hasUnresolvedErrors = (record: FlatStoRow): boolean => {
        return record.unresolvedItems.length > 0
    }

    // DISPLAY-only filter: the backend searches by RECORD, but each record flattens into rows for
    // all its STOs/users. Hide flattened rows that don't match the active per-STO params (user_id/
    // sto_id, AND-combined). Token/four_digits/date are record-level and never filter rows here.
    // The underlying records (and resolve's archive indices) are untouched.
    filterRowsBySearch = (flatRows: FlatStoRow[]): FlatStoRow[] => {
        const { searchUserId, searchStoId } = this.state
        const hasUserFilter = !!searchUserId
        const hasStoFilter = !!searchStoId
        if (!hasUserFilter && !hasStoFilter) return flatRows

        return flatRows.filter(row => {
            if (hasUserFilter) {
                if (row.user_id === null || String(row.user_id) !== String(searchUserId)) return false
            }
            if (hasStoFilter) {
                if (row.sto_id === null || String(row.sto_id) !== String(searchStoId)) return false
            }
            return true
        })
    }

    handleResolveProcess = async (
        id: number,
        index: number,
        action: ArchiveActionKey,
        resolve_status: 'error' | 'done' | 'handled' | 'no_handle_need'
    ) => {
        try {
            await replacedRows.handleReplacedCard({ id, archive: 'process', index, action, resolve_status })
            if (this._unmounted) return
            message.success('עודכן')
            this.fetchRows(this.state.page)
            this.loadApplyRequireDates()
        } catch (e) {
            if (this._unmounted) return
            message.error(extractErrorMessage(e, 'שגיאה בעדכון'))
        }
    }

    handleResolveError = async (id: number, index: number, resolved: boolean) => {
        try {
            await replacedRows.handleReplacedCard({ id, archive: 'error', index, resolved })
            if (this._unmounted) return
            message.success('עודכן')
            this.fetchRows(this.state.page)
            this.loadApplyRequireDates()
        } catch (e) {
            if (this._unmounted) return
            message.error(extractErrorMessage(e, 'שגיאה בעדכון'))
        }
    }

    renderStatus = (status: ReplacedCardStatus) => {
        if (status === 'APPLY_REQUIRE') return <Tag color="blue">ממתין לביצוע</Tag>
        if (status === 'HANDLE_REQUIRE') return <Tag color="red">נדרש טיפול</Tag>
        if (status === 'NO_HANDLE_REQUIRE') return <Tag>תקין</Tag>
        if (status === 'HANDLED') return <Tag color="green">טופל</Tag>
        return <span>—</span>
    }

    renderRowStatus = (record: FlatStoRow) => {
        if (record.status === 'HANDLE_REQUIRE' && record.allErrorsResolved) {
            return <Tag color="green">טופל</Tag>
        }
        return this.renderStatus(record.status)
    }

    handleCopyToken = (token: string | null) => {
        if (!token) return
        const clipboard = (navigator as any).clipboard
        if (!clipboard || !clipboard.writeText) {
            message.error('העתקה אינה נתמכת בדפדפן זה')
            return
        }
        clipboard
            .writeText(token)
            .then(() => message.success('הטוקן הועתק'))
            .catch(() => message.error('שגיאה בהעתקת הטוקן'))
    }

    renderFourDigits = (value: string | null, record: FlatStoRow) => {
        if (value === null || value === undefined || value === '') return <span>—</span>
        const text = String(value)
        if (!record.existing_token) {
            return (
                <span
                    className="replaced-cards-cell"
                    title={text}
                    style={{
                        backgroundColor: tokenColor(record.record_id),
                        borderRadius: 4,
                        color: '#262626',
                        display: 'inline-block',
                        fontWeight: 600,
                        padding: '2px 8px'
                    }}
                >
                    {text}
                </span>
            )
        }

        return (
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <Tooltip title="לחץ להעתקת הטוקן">
                    <span
                        className="replaced-cards-cell"
                        title={text}
                        style={{
                            backgroundColor: tokenColor(record.record_id),
                            borderRadius: 4,
                            color: '#262626',
                            cursor: 'pointer',
                            display: 'inline-block',
                            fontWeight: 600,
                            padding: '2px 8px'
                        }}
                        onClick={() => this.handleCopyToken(record.existing_token)}
                    >
                        {text}
                    </span>
                </Tooltip>
                <Tooltip title="חיפוש חיוב בטרנזילה">
                    <Icon
                        type="search"
                        style={{ color: '#1890ff', cursor: 'pointer', marginRight: 6 }}
                        onClick={event => {
                            event.stopPropagation()
                            openStoSearch('token', record.existing_token)
                        }}
                    />
                </Tooltip>
            </span>
        )
    }

    renderStoId = (stoId: string | number | null) => {
        if (stoId === null || stoId === undefined || stoId === '') return <span>—</span>
        const text = String(stoId)
        return (
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <span className="replaced-cards-cell" title={text}>
                    {text}
                </span>
                <Tooltip title="חיפוש חיוב בטרנזילה">
                    <Icon
                        type="search"
                        style={{ color: '#1890ff', cursor: 'pointer', marginRight: 6 }}
                        onClick={() => openStoSearch('stoId', stoId)}
                    />
                </Tooltip>
            </span>
        )
    }

    renderUserLink = (userId: number | null, userName: string | null) => {
        if (!userId) return <span>—</span>
        return (
            <span className="replaced-cards-user-cell">
                {userName && <span className="replaced-cards-user-name">{userName}</span>}
                <a href={`/clients/${userId}`} target="_blank" rel="noopener noreferrer">
                    {userId}
                </a>
            </span>
        )
    }

    renderErrors = (items: FlatErrorItem[], record: FlatStoRow) => {
        if (!items || items.length === 0) return <span>—</span>

        const resolveLocked = record.status === 'APPLY_REQUIRE'

        return (
            <div className="replaced-cards-errors">
                {items.map(item => {
                    const errorKey = this.getResolveKey(record.record_id, 'error', item.index)
                    const text = item.error_message || item.err_key || '—'
                    const resolveSwitch = (
                        <Switch
                            size="small"
                            disabled={resolveLocked}
                            checked={item.resolved === true}
                            onChange={checked => this.handleResolveError(record.record_id, item.index, checked)}
                        />
                    )
                    return (
                        <div key={errorKey} className="replaced-cards-error-item">
                            <Icon
                                type={item.resolved ? 'check-circle' : 'exclamation-circle'}
                                style={{ top: 'unset' }}
                                className={
                                    item.resolved
                                        ? 'replaced-cards-error-icon-resolved'
                                        : 'replaced-cards-error-icon-unresolved'
                                }
                            />
                            <span
                                className={
                                    item.resolved
                                        ? 'replaced-cards-error-message replaced-cards-error-message-resolved'
                                        : 'replaced-cards-error-message'
                                }
                            >
                                {text}
                            </span>
                            {/* antd Tooltip gets no mouse events from a disabled control — the span
                                receives the hover on its behalf. */}
                            {resolveLocked ? (
                                <Tooltip title={RESOLVE_LOCKED_TOOLTIP}>
                                    <span>{resolveSwitch}</span>
                                </Tooltip>
                            ) : (
                                resolveSwitch
                            )}
                        </div>
                    )
                })}
            </div>
        )
    }

    renderProcessSteps = (processEntry: ArchiveEntry | null, record: FlatStoRow) => {
        if (!processEntry || record.processIndex === null) return <span>—</span>
        const processIndex = record.processIndex
        const resolveLocked = record.status === 'APPLY_REQUIRE'
        return (
            <div className="replaced-cards-process-steps">
                {ACTION_LABELS.map(actionLabel => {
                    const action: ArchiveAction = processEntry[actionLabel.key] || { checked: false, done: false }
                    let color: string | undefined
                    let tagText: string
                    if (action.resolve_status) {
                        // Operator's explicit choice overrides the system-derived view.
                        const opt =
                            RESOLVE_STATUS_OPTIONS.filter(o => o.key === action.resolve_status)[0] ||
                            RESOLVE_STATUS_OPTIONS[0]
                        const who = action.resolved_by || record.applied_by || ''
                        const namePart = who && opt.color === 'green' ? `(${who}) ` : ''
                        color = opt.color
                        tagText = `${actionLabel.label}: ${opt.label}${namePart}`
                    } else if (action.done === true || action.resolved === true) {
                        const who = action.resolved === true ? action.resolved_by || '' : record.applied_by || ''
                        const namePart = who ? `(${who}) ` : ''
                        color = 'green'
                        tagText = `${actionLabel.label}: בוצע${namePart}`
                    } else if (action.checked && !action.done && !action.resolved) {
                        color = 'red'
                        tagText = `${actionLabel.label}: לא בוצע`
                    } else if (
                        !action.checked &&
                        action.skip_reason === 'manual_required' &&
                        action.resolved !== true
                    ) {
                        color = 'orange'
                        tagText = `${actionLabel.label}: נדרש טיפול ידני`
                    } else {
                        color = undefined
                        tagText = `${actionLabel.label}: לא נדרש טיפול`
                    }
                    const menu = (
                        <Menu
                            onClick={({ key }) =>
                                this.handleResolveProcess(
                                    record.record_id,
                                    processIndex,
                                    actionLabel.key,
                                    key as 'error' | 'done' | 'handled' | 'no_handle_need'
                                )
                            }
                        >
                            {RESOLVE_STATUS_OPTIONS.map(opt => (
                                <Menu.Item key={opt.key}>{opt.label}</Menu.Item>
                            ))}
                        </Menu>
                    )
                    const editButton = (
                        <Button
                            className="replaced-cards-process-resolve-button"
                            size="small"
                            icon="edit"
                            disabled={resolveLocked}
                        />
                    )
                    return (
                        <div className="replaced-cards-process-step-row" key={actionLabel.key}>
                            <Tag color={color}>{tagText}</Tag>
                            {/* antd Tooltip gets no mouse events from a disabled control — the span
                                receives the hover on its behalf. */}
                            {resolveLocked ? (
                                <Tooltip title={RESOLVE_LOCKED_TOOLTIP}>
                                    <span>{editButton}</span>
                                </Tooltip>
                            ) : (
                                <Dropdown overlay={menu} trigger={['click']}>
                                    {editButton}
                                </Dropdown>
                            )}
                        </div>
                    )
                })}
            </div>
        )
    }

    getColumns() {
        const center: 'center' = 'center'
        const dash = (v: any) => {
            if (v === null || v === undefined || v === '') return <span>—</span>
            const text = String(v)
            return (
                <span className="replaced-cards-cell" title={text}>
                    {text}
                </span>
            )
        }
        const fmt = (v: string | null) => {
            if (!v) return <span>—</span>
            const text = moment(v).format('DD-MM-YYYY HH:mm')
            return (
                <span className="replaced-cards-cell replaced-cards-date" title={text}>
                    {text}
                </span>
            )
        }
        const renderUpdateType = (_v: string, record: FlatStoRow) => {
            const typeLabel = UPDATE_TYPE_LABELS[record.update_type] || record.update_type
            if (record.update_type === 'unfrozen') {
                const resultLabel = PROCESSING_RESULT_LABELS[record.processing_result] || record.processing_result || ''
                const text = resultLabel ? `${typeLabel} (${resultLabel})` : typeLabel
                return dash(text)
            }
            return dash(typeLabel)
        }
        const renderSubscriptionStatus = (status: string | null) => {
            if (!status) return dash(status)
            return dash(SUBSCRIPTION_STATUS_LABELS[status] || status)
        }
        return [
            {
                title: 'תאריך',
                dataIndex: 'batch_date',
                key: 'batch_date',
                width: TABLE_COLUMN_WIDTHS.created_at,
                align: center,
                render: (_v: string | null, record: FlatStoRow) => {
                    const value = record.batch_date || record.created_at
                    if (!value) return <span>—</span>
                    const text = moment(value).format('DD-MM-YYYY') // date only (batch_date has no time)
                    return (
                        <span className="replaced-cards-cell replaced-cards-date" title={text}>
                            {text}
                        </span>
                    )
                }
            },
            {
                title: 'סוג עדכון',
                dataIndex: 'update_type',
                key: 'update_type',
                width: TABLE_COLUMN_WIDTHS.update_type,
                align: center,
                render: renderUpdateType
            },
            {
                title: '4 ספרות',
                dataIndex: 'existing_four_digits',
                key: 'existing_four_digits',
                width: TABLE_COLUMN_WIDTHS.existing_four_digits,
                align: center,
                render: (v: string | null, record: FlatStoRow) => this.renderFourDigits(v, record)
            },
            {
                title: 'סטטוס',
                dataIndex: 'status',
                key: 'status',
                width: TABLE_COLUMN_WIDTHS.status,
                align: center,
                render: (_v: ReplacedCardStatus, record: FlatStoRow) => this.renderRowStatus(record)
            },
            {
                title: 'מזהה משתמש',
                dataIndex: 'user_id',
                key: 'user_id',
                width: TABLE_COLUMN_WIDTHS.user_id,
                align: center,
                render: (v: number | null, record: FlatStoRow) => this.renderUserLink(v, record.user_name)
            },
            {
                title: 'מזהה חיוב',
                dataIndex: 'sto_id',
                key: 'sto_id',
                width: TABLE_COLUMN_WIDTHS.sto_id,
                render: (v: string | number | null) => this.renderStoId(v)
            },
            {
                title: 'סטטוס מנוי',
                dataIndex: 'sub_status',
                key: 'sub_status',
                width: TABLE_COLUMN_WIDTHS.sub_status,
                render: renderSubscriptionStatus
            },
            {
                title: 'שגיאות',
                dataIndex: 'errorItems',
                key: 'errorItems',
                width: TABLE_COLUMN_WIDTHS.errorItems,
                render: (items: FlatErrorItem[], record: FlatStoRow) => this.renderErrors(items, record)
            },
            {
                title: 'בוצע / לא בוצע',
                dataIndex: 'processEntry',
                key: 'processEntry',
                width: TABLE_COLUMN_WIDTHS.processEntry,
                render: (processEntry: ArchiveEntry | null, record: FlatStoRow) =>
                    this.renderProcessSteps(processEntry, record)
            },
            {
                title: 'בוצע ע״י',
                dataIndex: 'applied_by',
                key: 'applied_by',
                width: TABLE_COLUMN_WIDTHS.applied_by,
                render: dash
            },
            {
                title: 'בוצע בתאריך',
                dataIndex: 'applied_at',
                key: 'applied_at',
                width: TABLE_COLUMN_WIDTHS.applied_at,
                align: center,
                render: (v: string | null) => fmt(v)
            },
            {
                title: 'טופל ע״י',
                dataIndex: 'handled_by',
                key: 'handled_by',
                width: TABLE_COLUMN_WIDTHS.handled_by,
                className: 'replaced-cards-handled-by-column',
                render: dash
            },
            {
                title: 'טופל בתאריך',
                dataIndex: 'handled_at',
                key: 'handled_at',
                width: TABLE_COLUMN_WIDTHS.handled_at,
                align: center,
                render: (v: string | null) => fmt(v)
            }
        ]
    }

    renderSearchBlock() {
        const {
            mode,
            searchUpdateType,
            searchUserId,
            searchStoId,
            searchToken,
            searchFourDigits,
            searchDateFrom,
            searchDateTo
        } = this.state
        // 'unfrozen_check' already pins the type, so the whole group is moot there;
        // 'errors' only rules out 'מופשרים'.
        const typeGroupDisabled = mode === 'unfrozen_check'
        const unfrozenTypeDisabled = isUpdateTypeDisabled(mode, 'unfrozen')
        return (
            <div className="replaced-cards-search">
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                    <span style={{ display: 'flex', alignItems: 'center', marginLeft: 24 }}>
                        <span style={{ fontWeight: 500, marginLeft: 8 }}>סטטוס:</span>
                        <Radio.Group value={mode} onChange={e => this.handleModeChange(e.target.value)}>
                            <Radio value="errors">שגיאות</Radio>
                            <Radio value="unfrozen_check">הפשרות לבדיקה</Radio>
                            <Radio value="apply_require">ממתין לביצוע</Radio>
                            <Radio value="done">בוצע</Radio>
                            <Radio value="all">הכל</Radio>
                        </Radio.Group>
                    </span>
                    <span style={{ width: 1, height: 24, background: '#e8e8e8', marginLeft: 24 }} />
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontWeight: 500, marginLeft: 8 }}>סוג:</span>
                        {/* Tooltips wrap a span because a disabled control fires no mouse events. */}
                        {typeGroupDisabled ? (
                            <Tooltip title={FILTER_UNAVAILABLE_TOOLTIP}>
                                <span>
                                    <Radio.Group value={searchUpdateType} disabled>
                                        <Radio value="all">הכל</Radio>
                                        <Radio value="blocked">חסומים</Radio>
                                        <Radio value="unfrozen">מופשרים</Radio>
                                    </Radio.Group>
                                </span>
                            </Tooltip>
                        ) : (
                            <Radio.Group
                                value={searchUpdateType}
                                onChange={e => this.handleUpdateTypeChange(e.target.value)}
                            >
                                <Radio value="all">הכל</Radio>
                                <Radio value="blocked">חסומים</Radio>
                                {unfrozenTypeDisabled ? (
                                    <Tooltip title={FILTER_UNAVAILABLE_TOOLTIP}>
                                        <span>
                                            <Radio value="unfrozen" disabled>
                                                מופשרים
                                            </Radio>
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Radio value="unfrozen">מופשרים</Radio>
                                )}
                            </Radio.Group>
                        )}
                    </span>
                </div>

                <div className="replaced-cards-filters">
                    <Input
                        className="replaced-cards-filter-sm"
                        placeholder="מזהה משתמש"
                        value={searchUserId}
                        onPressEnter={this.handleSearch}
                        onChange={e => this.setState({ searchUserId: e.target.value.replace(/[^\d]/g, '') })}
                    />
                    <Input
                        className="replaced-cards-filter-sm"
                        placeholder="מזהה חיוב"
                        value={searchStoId}
                        onPressEnter={this.handleSearch}
                        onChange={e => this.setState({ searchStoId: e.target.value })}
                    />
                    <Input
                        className="replaced-cards-filter-md"
                        placeholder="טוקן"
                        value={searchToken}
                        onPressEnter={this.handleSearch}
                        onChange={e => this.setState({ searchToken: e.target.value })}
                    />
                    <Input
                        className="replaced-cards-filter-xs"
                        placeholder="4 ספרות"
                        value={searchFourDigits}
                        onPressEnter={this.handleSearch}
                        onChange={e => this.setState({ searchFourDigits: e.target.value })}
                    />
                    <RangePicker
                        className="replaced-cards-date-filter"
                        format="DD/MM/YYYY"
                        value={[searchDateFrom, searchDateTo] as any}
                        onChange={dates =>
                            this.setState({
                                searchDateFrom: dates && dates[0] ? dates[0] : null,
                                searchDateTo: dates && dates[1] ? dates[1] : null
                            })
                        }
                    />
                    <Button type="primary" icon="search" onClick={this.handleSearch}>
                        חיפוש
                    </Button>
                    <Button onClick={this.resetSearch}>איפוס</Button>
                </div>
            </div>
        )
    }

    getTableHeight = (): number => {
        const reserved = 360
        const height = (typeof window !== 'undefined' ? window.innerHeight : 800) - reserved
        return height > 300 ? height : 300
    }

    render() {
        const {
            authStore: { roles }
        } = this.props

        const { openCsvUploaderModal, applyRequireDates, rows, loading, page, perPage, total } = this.state
        const safeRoles = roles || []
        const hasAcceptedRole = safeRoles.includes('admin') || safeRoles.includes('money')
        const flatRows = this.filterRowsBySearch(flattenReplacedCardRows(rows))

        return (
            <Layout>
                <style>{pageStyles}</style>
                <CsvUploader
                    visible={openCsvUploaderModal || this.state.applyModalOpen}
                    applyMode={this.state.applyModalOpen}
                    applyDates={this.state.applyRequireDates}
                    onClose={() =>
                        this.setState({ openCsvUploaderModal: false, applyModalOpen: false }, () => {
                            this.fetchRows(1)
                            this.loadApplyRequireDates()
                        })
                    }
                />

                <PageHeader
                    onBack={() => window.history.back()}
                    title="מוחלפים"
                    extra={[
                        <div
                            key="header-actions"
                            style={{ display: 'flex', gap: 8, position: 'absolute', left: '24px' }}
                        >
                            <Button
                                key="run-apply"
                                style={{ display: hasAcceptedRole && applyRequireDates.length > 0 ? '' : 'none' }}
                                onClick={() => this.setState({ applyModalOpen: true })}
                            >
                                <Icon type="play-circle" />
                                <span>הרץ שלב ביצוע</span>
                            </Button>
                            <Button
                                key="csv-uploader"
                                style={{ display: hasAcceptedRole ? '' : 'none' }}
                                onClick={() => this.setState({ openCsvUploaderModal: true })}
                            >
                                <Icon type="upload" />
                                <span>קובץ מוחלפים</span>
                            </Button>
                        </div>
                    ]}
                />

                <div className="replaced-cards-page">
                    {this.renderSearchBlock()}

                    <div className="replaced-cards-table-shell">
                        <Table
                            rowKey="key"
                            dataSource={flatRows}
                            columns={this.getColumns()}
                            loading={loading}
                            size="small"
                            className={`replaced-cards-table center-table ${tableClass}`}
                            scroll={{ x: TABLE_SCROLL_X, y: this.getTableHeight() }}
                            bordered={true}
                            rowClassName={(record: FlatStoRow) =>
                                this.hasUnresolvedErrors(record) ? 'replaced-cards-row-error' : ''
                            }
                            locale={{ emptyText: 'אין נתונים להצגה' }}
                            pagination={false}
                        />
                        {/* Paginate by RECORDS (server total). The Table renders ALL expanded
                            per-STO rows for the current 20-record page — no client-side slicing. */}
                        <div style={{ textAlign: 'left', padding: '12px 16px' }}>
                            <Pagination
                                current={page}
                                pageSize={perPage}
                                total={total}
                                showSizeChanger={false}
                                showTotal={(totalRows: number) => `סה״כ ${totalRows} רשומות`}
                                onChange={(p: number) => this.fetchRows(p)}
                            />
                        </div>
                    </div>
                </div>
            </Layout>
        )
    }
}

export default withRouter(ReplacedCards)
